/**
 * Who is on the other end of a call, as shown in the call-base list.
 *
 * One column replaces the old «مشتری» + «شماره تماس» pair:
 * - a customer  → the customer's name, linking to the customer file;
 * - a lead      → the lead's title, linking to the lead file;
 * - nobody yet  → the bare number, with an action to add it to the system.
 */
export type CallContactKind = 'customer' | 'lead' | 'unknown';

export interface CallContactSource {
  readonly partyName: string;
  readonly partyCode?: string | null | undefined;
  readonly leadRef?: string | null | undefined;
  readonly leadTitle?: string | null | undefined;
  readonly endpoint: string;
}

export interface CallContact {
  readonly kind: CallContactKind;
  /** Name to show; for `unknown` it is the phone number itself. */
  readonly label: string;
  /** Customer code or lead code; `null` for an unknown caller. */
  readonly ref: string | null;
  readonly phone: string;
}

const present = (v: string | null | undefined): v is string =>
  typeof v === 'string' && v.trim().length > 0;

export function callContactOf(row: CallContactSource): CallContact {
  if (present(row.partyCode)) {
    return {
      kind: 'customer',
      label: present(row.partyName) ? row.partyName : row.partyCode,
      ref: row.partyCode,
      phone: row.endpoint,
    };
  }
  if (present(row.leadRef)) {
    return {
      kind: 'lead',
      label: present(row.leadTitle) ? row.leadTitle : row.leadRef,
      ref: row.leadRef,
      phone: row.endpoint,
    };
  }
  return { kind: 'unknown', label: row.endpoint, ref: null, phone: row.endpoint };
}

/** The file of a customer or a lead, under the tenant base (`/t/{slug}`). */
export function partyFileHref(kind: 'customer' | 'lead', ref: string, base: string): string {
  return kind === 'customer'
    ? `${base}/crm/customers/${encodeURIComponent(ref)}`
    : `${base}/crm/leads?lead=${encodeURIComponent(ref)}`;
}

/** Route of the contact's own file, relative to the tenant base (`/t/{slug}`); `null` when none exists. */
export function callContactHref(contact: CallContact, base: string): string | null {
  if (contact.ref === null || contact.kind === 'unknown') return null;
  return partyFileHref(contact.kind, contact.ref, base);
}
